import React, {useState, useEffect, useCallback} from 'react';
import { Link } from 'react-router-dom';
import PanelNotice from './PanelNotice';
import openingHoursSvc from '../services/openingHours';

// SETTINGS » מועדים מיוחדים וחסימות (exceptions only).
// The REGULAR weekly opening hours (type-1 rows) are edited in ONE place only:
// "ניהול העסק" » שעות פעילות (BusinessContent). This screen manages ONLY the
// exceptions on top of them — type 2 = one-time OPEN windows, type 3 = BLOCKED
// windows — so the two editors never write the same rows (no duplicate/conflict).
// CRITICAL: loadedIds and the save delete-set contain ONLY type 2/3 row IDs, so
// saving here can never delete or overwrite the weekly (type-1) hours.
function BusinessWeekScheduler() {
    const [specificTimes, setSpecificTimes] = useState([{ fromDate: '', toDate: '', fromTime: '', toTime: '' }]);
    const [blockedTimes, setBlockedTimes] = useState([{ fromDate: '', toDate: '', fromTime: '', toTime: '' }]);
    const [loadedIds, setLoadedIds] = useState([]); // type 2/3 row IDs currently persisted
    const [saving, setSaving] = useState(false);
    const [notice, setNotice] = useState('');

    // Load ONLY the exception rows (type 2/3). Type-1 weekly rows are intentionally
    // ignored here (and their IDs are NOT collected) so this screen never touches them.
    const loadHours = useCallback(async () => {
        const rows = await openingHoursSvc.listHours();
        const ids = []; const spec = []; const blocked = [];
        rows.forEach((r) => {
            const type = Number(r.type);
            const st = String(r.startTime || '').slice(0, 5);
            const et = String(r.endTime || '').slice(0, 5);
            // Only ever collect a valid positive numeric ID (never undefined) so the
            // save-time delete set can't include a bogus id.
            const rowId = Number(r.ID ?? r.id);
            const validId = Number.isInteger(rowId) && rowId > 0;
            if (type === 2) {
                if (validId) ids.push(rowId);
                spec.push({ fromDate: r.startingDate || '', toDate: r.endingDate || '', fromTime: st, toTime: et });
            } else if (type === 3) {
                if (validId) ids.push(rowId);
                blocked.push({ fromDate: r.startingDate || '', toDate: r.endingDate || '', fromTime: st, toTime: et });
            }
        });
        setLoadedIds(ids);
        setSpecificTimes(spec.length ? spec : [{ fromDate: '', toDate: '', fromTime: '', toTime: '' }]);
        setBlockedTimes(blocked.length ? blocked : [{ fromDate: '', toDate: '', fromTime: '', toTime: '' }]);
    }, []);

    useEffect(() => { loadHours(); }, [loadHours]);

    // Save = replace-all for EXCEPTIONS ONLY: delete the currently-persisted type 2/3
    // rows (loadedIds holds only those), then insert the current editor state fresh.
    // Type-1 weekly rows are never in loadedIds and no type-1 rows are added here.
    const handleSave = async () => {
        setSaving(true);
        const delResults = await Promise.all(loadedIds.map((id) => openingHoursSvc.delHour(id)));
        const adds = [];
        specificTimes.forEach((s) => {
            if (s.fromDate && s.toDate && s.fromTime && s.toTime) {
                adds.push({ type: 2, area: 0, startingDate: s.fromDate, endingDate: s.toDate, startTime: `${s.fromTime}:00`, endTime: `${s.toTime}:00`, description: 'פתוח' });
            }
        });
        blockedTimes.forEach((b) => {
            if (b.fromDate && b.toDate && b.fromTime && b.toTime) {
                adds.push({ type: 3, area: 0, startingDate: b.fromDate, endingDate: b.toDate, startTime: `${b.fromTime}:00`, endTime: `${b.toTime}:00`, description: 'סגור' });
            }
        });
        const addResults = await Promise.all(adds.map((a) => openingHoursSvc.addHour(a)));
        const failed = delResults.filter((r) => !r || !r.ok).length + addResults.filter((r) => !r || !r.ok).length;
        await loadHours(); // reflect server truth + refresh the persisted IDs
        setSaving(false);
        setNotice(failed ? `נשמר חלקית — ${failed} רשומות נכשלו` : 'המועדים המיוחדים נשמרו ✓');
        setTimeout(() => setNotice(''), 3000);
    };

    const handleSpecificTimeChange = (index, type, value) => {
        setSpecificTimes(prevSpecificTimes =>
            prevSpecificTimes.map((specificTime, i) =>
                i === index ? { ...specificTime, [type]: value } : specificTime
            )
        );
    };

    const addSpecificTimeRange = () => {
        setSpecificTimes(prevSpecificTimes => [
            ...prevSpecificTimes,
            { fromDate: '', toDate: '', fromTime: '', toTime: '' }
        ]);
    };

    const removeSpecificTimeRange = (index) => {
        setSpecificTimes(prevSpecificTimes => prevSpecificTimes.filter((_, i) => i !== index));
    };

    const handleBlockedTimeChange = (index, type, value) => {
        setBlockedTimes(prevBlockedTimes =>
            prevBlockedTimes.map((blockedTime, i) =>
                i === index ? { ...blockedTime, [type]: value } : blockedTime
            )
        );
    };

    const addBlockedTimeRange = () => {
        setBlockedTimes(prevBlockedTimes => [
            ...prevBlockedTimes,
            { fromDate: '', toDate: '', fromTime: '', toTime: '' }
        ]);
    };

    const removeBlockedTimeRange = (index) => {
        setBlockedTimes(prevBlockedTimes => prevBlockedTimes.filter((_, i) => i !== index));
    };

    const generateTimeOptions = () => {
        const options = [];
        for (let hour = 0; hour < 24; hour++) {
            for (let minutes = 0; minutes < 60; minutes += 5) {
                const timeString = `${hour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
                options.push(<option key={timeString} value={timeString}>{timeString}</option>);
            }
        }
        return options;
    };

    return (
        <div className="WeekScheduler flex">
            {notice && <PanelNotice>{notice}</PanelNotice>}

            {/* The regular weekly hours live in ONE place — point owners there. */}
            <div className="hours-relocate-note">
                שעות הפעילות הקבועות (השבועיות) נערכות במסך
                {' '}<Link to="/business" className="hours-relocate-link">ניהול העסק » שעות פעילות</Link>.
                כאן מגדירים רק פתיחות חריגות וחסימות של תאריכים ספציפיים.
            </div>

            <div className="week-times-title-container">
                <h3>מועדים מיוחדים וחסימות</h3>
                <button type="button" className="save-range-button" onClick={handleSave} disabled={saving}>{saving ? 'שומר…' : 'שמור'}</button>
            </div>

            <div className={'flex special-events'}>
                <div className="specific-time-container">
                    <div className={'specieal-event-title'}>
                        <h3>פתיחה חריגה</h3>
                        <button type="button" className="add-time-range-button" onClick={addSpecificTimeRange}>+</button>
                    </div>
                    {specificTimes.map((specificTime, index) => (
                        <div key={index} className="specific-time-range">
                            <label>
                                 מתאריך:
                                <input
                                    type="date"
                                    value={specificTime.fromDate}
                                    onChange={(e) => handleSpecificTimeChange(index, 'fromDate', e.target.value)}
                                />
                            </label>
                            <label>
                                עד תאריך:
                                <input
                                    type="date"
                                    value={specificTime.toDate}
                                    onChange={(e) => handleSpecificTimeChange(index, 'toDate', e.target.value)}
                                />
                            </label>
                            <label>
                                משעה:
                                <select
                                    value={specificTime.fromTime}
                                    onChange={(e) => handleSpecificTimeChange(index, 'fromTime', e.target.value)}
                                >
                                    {generateTimeOptions()}
                                </select>
                            </label>
                            <label>
                                עד שעה:
                                <select
                                    value={specificTime.toTime}
                                    onChange={(e) => handleSpecificTimeChange(index, 'toTime', e.target.value)}
                                >
                                    {generateTimeOptions()}
                                </select>
                            </label>
                            {index > 0 && (
                                <button
                                    type="button"
                                    className="remove-time-range-button"
                                    onClick={() => removeSpecificTimeRange(index)}
                                >
                                    -
                                </button>
                            )}
                        </div>
                    ))}
                </div>
                <div className="blocked-time-container">
                    <div className={'specieal-event-title'}>
                        <h3>חסימת פעילות</h3>
                        <button type="button" className="add-time-range-button" onClick={addBlockedTimeRange}>+</button>
                    </div>
                    {blockedTimes.map((blockedTime, index) => (
                        <div key={index} className="specific-time-range">
                            <label>
                                מתאריך:
                                <input
                                    type="date"
                                    value={blockedTime.fromDate}
                                    onChange={(e) => handleBlockedTimeChange(index, 'fromDate', e.target.value)}
                                />
                            </label>
                            <label>
                                עד תאריך:
                                <input
                                    type="date"
                                    value={blockedTime.toDate}
                                    onChange={(e) => handleBlockedTimeChange(index, 'toDate', e.target.value)}
                                />
                            </label>
                            <label>
                                משעה:
                                <select
                                    value={blockedTime.fromTime}
                                    onChange={(e) => handleBlockedTimeChange(index, 'fromTime', e.target.value)}
                                >
                                    {generateTimeOptions()}
                                </select>
                            </label>
                            <label>
                                עד שעה:
                                <select
                                    value={blockedTime.toTime}
                                    onChange={(e) => handleBlockedTimeChange(index, 'toTime', e.target.value)}
                                >
                                    {generateTimeOptions()}
                                </select>
                            </label>
                            {index > 0 && (
                                <button
                                    type="button"
                                    className="remove-time-range-button"
                                    onClick={() => removeBlockedTimeRange(index)}
                                >
                                    -
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
export default BusinessWeekScheduler;
